import { isObject } from './object-utils';

/**
 * Computes the SHA-256 hash of a string and returns it as a hex string.
 *
 * This function is designed to work in both Node.js and browser environments.
 * However, it should be avoided in known server-only contexts. In those cases,
 * use the `node:crypto` module directly because it is substantially faster.
 *
 * @param data - The string to hash.
 * @returns A promise that resolves to the hex string representation of the SHA-256 hash.
 */
export const sha256Hex = async (data: string): Promise<string> => {
  if (typeof window === 'undefined') {
    const { hash } = await import('node:crypto');
    return hash('sha256', data, 'hex');
  }

  const hashBuffer = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(data)
  );

  let result = '';
  for (const byte of new Uint8Array(hashBuffer)) {
    // Convert byte to hex and ensure two-digit representation
    result += byte.toString(16).padStart(2, '0');
  }

  return result;
};

/**
 * Generates a stable hash string from the provided values, suitable for use as a query key.
 * This only supports JSON-serializable values (e.g. no Maps, Sets, functions, symbols,
 * circular references, etc.).
 * @param keys - The values to hash. The first value must be defined.
 * @returns A string hash representing the provided values.
 */
export function hashKeys(keys: readonly [unknown, ...Array<unknown>]): string {
  return JSON.stringify(keys, (_, val): unknown =>
    isObject(val)
      ? Object.fromEntries(
          Object.entries(val).sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        )
      : val
  );
}
